import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  fetchInfluencerEarnings, 
  fetchInfluencerCampaigns 
} from '../../services/dashboardService';
import { 
  subscribeToDashboardUpdates, 
  subscribeToEarningsUpdates 
} from '../../services/realtimeService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import QuickActions from '../../components/ui/QuickActions';
import EarningsPanel from './components/EarningsPanel';
import ProposalCard from './components/ProposalCard';
import ServicePricingCard from './components/ServicePricingCard';
import PortfolioShowcase from './components/PortfolioShowcase';
import SocialMediaStats from './components/SocialMediaStats';
import ActiveCampaigns from './components/ActiveCampaigns';
import MessagingInterface from './components/MessagingInterface';
import Icon from '../../components/AppIcon';

const InfluencerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [earningsData, setEarningsData] = useState(null);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [influencerId, setInfluencerId] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
      fetchInfluencerId();
    }
  }, [user]);

  // Fetch influencer ID for subscriptions
  const fetchInfluencerId = async () => {
    try {
      const { data } = await supabase
        ?.from('influencers')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.single();
      
      if (data?.id) {
        setInfluencerId(data?.id);
      }
    } catch (error) {
      console.error('Error fetching influencer ID:', error);
    }
  };

  // Real-time subscriptions
  useEffect(() => {
    if (!user?.id || !influencerId) return;

    const dashboardSubscription = subscribeToDashboardUpdates(user?.id, {
      onNotificationUpdate: (payload) => {
        console.log('Notification update received:', payload);
        // You can show a toast notification here
      },
      onInfluencerUpdate: (payload) => {
        console.log('Influencer profile update received:', payload);
        // Reload dashboard data when profile changes
        loadDashboardData();
      }
    });

    const earningsSubscription = subscribeToEarningsUpdates(influencerId, (payload) => {
      console.log('Earnings update received:', payload);
      // Reload earnings and campaigns when earnings-related data changes
      loadEarningsData();
      loadCampaignsData();
    });

    return () => {
      dashboardSubscription?.unsubscribe();
      earningsSubscription?.unsubscribe();
    };
  }, [user?.id, influencerId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all dashboard data in parallel
      const [earnings, campaigns] = await Promise.all([
        fetchInfluencerEarnings(user?.id),
        fetchInfluencerCampaigns(user?.id)
      ]);

      setEarningsData(earnings);
      setActiveCampaigns(campaigns);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEarningsData = async () => {
    try {
      const earnings = await fetchInfluencerEarnings(user?.id);
      setEarningsData(earnings);
    } catch (error) {
      console.error('Error loading earnings:', error);
    }
  };

  const loadCampaignsData = async () => {
    try {
      const campaigns = await fetchInfluencerCampaigns(user?.id);
      setActiveCampaigns(campaigns);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const proposals = [
  {
    id: 1,
    artistName: "Ahmet Yılmaz",
    artistImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1c710bfb6-1763294397789.png",
    artistImageAlt: "Professional headshot of Turkish male artist with short dark hair wearing black leather jacket in studio setting",
    campaignTitle: "Yeni Single Lansmanı",
    genre: "Pop",
    offer: 5500.00,
    deadline: "2025-12-15",
    status: "new",
    deliverables: ["Instagram Story", "Reels", "Post"],
    description: "Yeni çıkan single\'ımız için Instagram\'da kapsamlı bir tanıtım kampanyası düzenlemek istiyoruz. Hedef kitlemiz 18-35 yaş arası müzik severler."
  },
  {
    id: 2,
    artistName: "Zeynep Demir",
    artistImage: "https://img.rocket.new/generatedImages/rocket_gen_img_158ebe045-1763294644032.png",
    artistImageAlt: "Professional portrait of Turkish female artist with long brown hair wearing elegant white blouse in modern studio",
    campaignTitle: "Albüm Tanıtımı",
    genre: "R&B",
    offer: 8750.00,
    deadline: "2025-12-20",
    status: "pending",
    deliverables: ["YouTube Video", "Instagram Post", "TikTok"],
    description: "Yeni albümümüzün tanıtımı için çoklu platform stratejisi. Özellikle genç dinleyicilere ulaşmak istiyoruz."
  },
  {
    id: 3,
    artistName: "Can Özkan",
    artistImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0e3bd81-1763292600429.png",
    artistImageAlt: "Professional photo of Turkish male musician with beard wearing casual denim jacket in urban outdoor setting",
    campaignTitle: "Konser Duyurusu",
    genre: "Rock",
    offer: 3200.00,
    deadline: "2025-12-10",
    status: "new",
    deliverables: ["Story", "Post"],
    description: "İstanbul\'da düzenleyeceğimiz konser için bilet satışlarını artırmak amacıyla sosyal medya tanıtımı."
  }];


  const services = [
  {
    id: 1,
    type: "video",
    name: "YouTube Video",
    description: "Tam uzunlukta tanıtım videosu",
    price: 12500.00,
    ordersCount: 8
  },
  {
    id: 2,
    type: "story",
    name: "Instagram Story",
    description: "24 saatlik story paylaşımı",
    price: 2500.00,
    ordersCount: 24
  },
  {
    id: 3,
    type: "post",
    name: "Instagram Post",
    description: "Kalıcı feed paylaşımı",
    price: 4500.00,
    ordersCount: 15
  },
  {
    id: 4,
    type: "reel",
    name: "Instagram Reels",
    description: "Kısa format video içeriği",
    price: 3800.00,
    ordersCount: 19
  }];


  const portfolioItems = [
  {
    id: 1,
    thumbnail: "https://images.unsplash.com/photo-1720968638468-98b9b215c232",
    thumbnailAlt: "Young woman with headphones dancing energetically in colorful neon-lit urban street at night with vibrant purple and blue lighting",
    title: "Yaz Hiti Tanıtımı",
    platform: "Instagram",
    date: "15.11.2025",
    likes: 45600,
    comments: 892,
    shares: 234,
    engagementRate: 6.8
  },
  {
    id: 2,
    thumbnail: "https://images.unsplash.com/photo-1695138343365-20c2bc017bc3",
    thumbnailAlt: "Professional music producer working at modern recording studio with multiple monitors and mixing equipment in dimly lit environment",
    title: "Stüdyo Kayıt Süreci",
    platform: "YouTube",
    date: "12.11.2025",
    likes: 128000,
    comments: 3421,
    shares: 1567,
    engagementRate: 8.2
  },
  {
    id: 3,
    thumbnail: "https://images.unsplash.com/photo-1651439364160-6f7989b33a4b",
    thumbnailAlt: "Energetic live concert performance with artist on stage under dramatic blue and purple stage lighting with crowd silhouettes",
    title: "Konser Anları",
    platform: "TikTok",
    date: "10.11.2025",
    likes: 234000,
    comments: 5678,
    shares: 8901,
    engagementRate: 12.4
  },
  {
    id: 4,
    thumbnail: "https://images.unsplash.com/photo-1588693555756-be8100e85641",
    thumbnailAlt: "Close-up of hands playing acoustic guitar with warm golden lighting highlighting strings and wooden texture in intimate setting",
    title: "Akustik Cover",
    platform: "Instagram",
    date: "08.11.2025",
    likes: 67800,
    comments: 1234,
    shares: 456,
    engagementRate: 7.1
  },
  {
    id: 5,
    thumbnail: "https://images.unsplash.com/photo-1631531664852-d114018ae2a0",
    thumbnailAlt: "DJ performing at electronic music festival with colorful laser lights and smoke effects creating dynamic atmosphere on stage",
    title: "Festival Performansı",
    platform: "YouTube",
    date: "05.11.2025",
    likes: 189000,
    comments: 4567,
    shares: 2345,
    engagementRate: 9.6
  },
  {
    id: 6,
    thumbnail: "https://images.unsplash.com/photo-1516734668183-dcbd209d15e0",
    thumbnailAlt: "Young musician with electric guitar in urban graffiti-covered alley wearing casual streetwear with dramatic natural lighting",
    title: "Sokak Müziği",
    platform: "TikTok",
    date: "02.11.2025",
    likes: 345000,
    comments: 8901,
    shares: 12345,
    engagementRate: 15.2
  }];


  const socialPlatforms = [
  {
    name: "Instagram",
    username: "muzik_influencer",
    followers: 485000,
    engagement: 7.2,
    reach: 1200000,
    verified: true,
    lastUpdated: "30.11.2025"
  },
  {
    name: "YouTube",
    username: "MuzikKanali",
    followers: 320000,
    engagement: 5.8,
    reach: 890000,
    verified: true,
    lastUpdated: "29.11.2025"
  },
  {
    name: "TikTok",
    username: "muzik_pro",
    followers: 750000,
    engagement: 12.4,
    reach: 2500000,
    verified: true,
    lastUpdated: "30.11.2025"
  }];


  const activeCampaignsData = [
  {
    id: 1,
    title: "Elektronik Müzik Festivali",
    artistName: "DJ Emre",
    artistImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1decb7846-1763301419131.png",
    artistImageAlt: "Professional photo of Turkish male DJ with short styled hair wearing black t-shirt in modern studio with equipment",
    payment: 15000.00,
    deadline: "2025-12-08",
    status: "in-progress",
    progress: 65,
    milestones: [
    { name: "Story", completed: true },
    { name: "Reels", completed: true },
    { name: "Post", completed: false }]

  },
  {
    id: 2,
    title: "Akustik Konser Serisi",
    artistName: "Selin Aydın",
    artistImage: "https://img.rocket.new/generatedImages/rocket_gen_img_104ccf6fc-1763296410498.png",
    artistImageAlt: "Professional portrait of Turkish female singer with wavy brown hair wearing elegant burgundy dress in concert hall setting",
    payment: 8500.00,
    deadline: "2025-12-12",
    status: "review",
    progress: 90,
    milestones: [
    { name: "Video", completed: true },
    { name: "Story", completed: true },
    { name: "Post", completed: true }]

  }];


  const conversations = [
  {
    id: 1,
    name: "Ahmet Yılmaz",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c710bfb6-1763294397789.png",
    avatarAlt: "Professional headshot of Turkish male artist with short dark hair wearing black leather jacket in studio setting",
    lastMessage: "Kampanya detaylarını görüşebilir miyiz?",
    lastMessageTime: new Date(Date.now() - 300000),
    unread: 2,
    messages: [
    { sender: 'them', text: 'Merhaba, teklifinizi aldım.', time: new Date(Date.now() - 3600000) },
    { sender: 'me', text: 'Merhaba! Teşekkür ederim. Detayları görüşelim.', time: new Date(Date.now() - 3000000) },
    { sender: 'them', text: 'Kampanya detaylarını görüşebilir miyiz?', time: new Date(Date.now() - 300000) }]

  },
  {
    id: 2,
    name: "Zeynep Demir",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_158ebe045-1763294644032.png",
    avatarAlt: "Professional portrait of Turkish female artist with long brown hair wearing elegant white blouse in modern studio",
    lastMessage: "İçerik taslağını onayladım.",
    lastMessageTime: new Date(Date.now() - 7200000),
    unread: 0,
    messages: [
    { sender: 'them', text: 'İçerik taslağını onayladım.', time: new Date(Date.now() - 7200000) }]

  },
  {
    id: 3,
    name: "Can Özkan",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0e3bd81-1763292600429.png",
    avatarAlt: "Professional photo of Turkish male musician with beard wearing casual denim jacket in urban outdoor setting",
    lastMessage: "Ödeme ne zaman yapılacak?",
    lastMessageTime: new Date(Date.now() - 86400000),
    unread: 1,
    messages: [
    { sender: 'them', text: 'Ödeme ne zaman yapılacak?', time: new Date(Date.now() - 86400000) }]

  }];


  const handleAcceptProposal = (proposalId) => {
    console.log('Accepting proposal:', proposalId);
  };

  const handleRejectProposal = (proposalId) => {
    console.log('Rejecting proposal:', proposalId);
  };

  const handleNegotiateProposal = (proposalId) => {
    console.log('Negotiating proposal:', proposalId);
  };

  const handleViewProposalDetails = (proposalId) => {
    navigate(`/campaign-management?proposal=${proposalId}`);
  };

  const handleUpdateService = (serviceId, newPrice) => {
    console.log('Servis güncellendi:', serviceId, newPrice);
  };

  const handleViewCampaignDetails = (campaignId) => {
    navigate(`/campaign-management?id=${campaignId}`);
  };

  const tabs = [
  { id: 'overview', label: 'Genel Bakış', icon: 'LayoutDashboard' },
  { id: 'proposals', label: 'Teklifler', icon: 'FileText' },
  { id: 'campaigns', label: 'Kampanyalar', icon: 'Briefcase' },
  { id: 'services', label: 'Hizmetler', icon: 'Package' },
  { id: 'portfolio', label: 'Portföy', icon: 'Image' },
  { id: 'messages', label: 'Mesajlar', icon: 'MessageSquare' }];


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 pb-20 md:pb-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">Influencer Dashboard</h1>
          <p className="text-muted-foreground">Kampanyalarınızı yönetin ve kazançlarınızı takip edin</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-smooth whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Tab Content */}
        {!loading && (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Earnings Panel */}
                {earningsData && <EarningsPanel earnings={earningsData} />}

                {/* Empty State for Earnings */}
                {!earningsData?.total && earningsData?.total === 0 && (
                  <div className="bg-card border border-border rounded-lg p-8 text-center">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="DollarSign" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Henüz kazancınız yok</h3>
                    <p className="text-muted-foreground mb-4">Kampanyalara katılarak kazanmaya başlayın</p>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Active Campaigns */}
                  <div>
                    {activeCampaigns?.length === 0 ? (
                      <div className="bg-card border border-border rounded-lg p-8 text-center">
                        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="Briefcase" size={32} className="text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">Aktif kampanya yok</h3>
                        <p className="text-muted-foreground mb-4">Teklifleri kabul ederek kampanyalara katılın</p>
                      </div>
                    ) : (
                      <ActiveCampaigns 
                        campaigns={activeCampaigns} 
                        onViewDetails={handleViewCampaignDetails}
                      />
                    )}
                  </div>

                  {/* Social Media Stats */}
                  <div>
                    {socialPlatforms?.length === 0 ? (
                      <div className="bg-card border border-border rounded-lg p-8 text-center">
                        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="Share2" size={32} className="text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">Sosyal medya hesapları eklenmemiş</h3>
                        <p className="text-muted-foreground mb-4">Profilinizi tamamlamak için sosyal medya hesaplarınızı ekleyin</p>
                        <button 
                          onClick={() => navigate('/social-links-setup')}
                          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
                        >
                          Hesap Ekle
                        </button>
                      </div>
                    ) : (
                      <SocialMediaStats platforms={socialPlatforms} />
                    )}
                  </div>
                </div>

                {/* Services */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Hizmetlerim</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {services?.map((service) => (
                      <ServicePricingCard 
                        key={service?.id} 
                        service={service} 
                        onUpdate={handleUpdateService}
                      />
                    ))}
                  </div>
                </div>

                {/* Portfolio */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Portföy</h2>
                  </div>
                  <PortfolioShowcase items={portfolioItems} />
                </div>
              </div>
            )}

            {/* Campaigns Tab */}
            {activeTab === 'campaigns' && (
              <div>
                {activeCampaigns?.length === 0 ? (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Briefcase" size={40} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Henüz aktif kampanyanız yok</h3>
                    <p className="text-muted-foreground mb-6">Teklifleri kabul ederek kampanyalara katılmaya başlayın</p>
                  </div>
                ) : (
                  <ActiveCampaigns 
                    campaigns={activeCampaigns} 
                    onViewDetails={handleViewCampaignDetails}
                  />
                )}
              </div>
            )}

            {/* Proposals Tab */}
            {activeTab === 'proposals' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {proposals?.map((proposal) => (
                    <ProposalCard
                      key={proposal?.id}
                      proposal={proposal}
                      onAccept={handleAcceptProposal}
                      onReject={handleRejectProposal}
                      onNegotiate={handleNegotiateProposal}
                      onViewDetails={handleViewProposalDetails}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div>
                <MessagingInterface conversations={conversations} />
              </div>
            )}
          </>
        )}
      </div>
      <MobileNav />
      <QuickActions />
    </div>
  );
};

export default InfluencerDashboard;